import { forwardRef } from "react";
import { BookMarked, BookOpen, ExternalLink, Landmark, Loader2, Wrench } from "lucide-react";

const SUGGESTION_ICONS = {
  framework: BookOpen,
  tool: Wrench,
  india: Landmark,
  glossary: BookMarked,
};

function SuggestionList({ suggestions, msg, msgIndex, onTrackClick }) {
  return (
    <div className="max-w-[85%] mt-2 space-y-1.5" data-testid={`chatbot-suggestions-${msgIndex}`}>
      <div className="text-[10px] uppercase tracking-wider text-rp-textFaint flex items-center gap-1">
        <ExternalLink className="h-3 w-3" />
        Explore on RiskPedia
      </div>
      {suggestions.map((s, j) => {
        const Icon = SUGGESTION_ICONS[s.type] || ExternalLink;
        return (
          <a
            key={s.url || `s-${j}`}
            href={s.url}
            target="_blank"
            rel="noreferrer"
            onClick={() => onTrackClick(s, msg)}
            className="block rounded-lg border border-rp-border bg-rp-bg hover:border-emerald-500/40 hover:bg-emerald-900/10 p-2 transition-colors"
            data-testid={`chatbot-suggestion-${msgIndex}-${j}`}
          >
            <div className="flex items-start gap-2">
              <Icon className="h-3.5 w-3.5 text-emerald-300 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-rp-text truncate">{s.title}</div>
                {s.teaser && (
                  <div className="text-[10px] text-rp-textDim line-clamp-2 mt-0.5">{s.teaser}</div>
                )}
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );
}

export const ChatbotMessages = forwardRef(function ChatbotMessages(
  { messages, sending, activePersonaName, expanded, onTrackClick },
  ref,
) {
  return (
    <div
      ref={ref}
      className="flex-1 overflow-y-auto px-4 py-3"
      data-testid="chatbot-messages"
    >
      <div className={expanded ? "max-w-3xl mx-auto w-full space-y-3" : "space-y-3"}>
        {messages.map((m, i) => (
          <div
            key={m.id || `m-${i}`}
            className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                m.role === "user"
                  ? "bg-emerald-600 text-white"
                  : "bg-rp-bg border border-rp-border text-rp-text"
              }`}
              data-testid={`chatbot-msg-${m.role}-${i}`}
            >
              {m.content}
            </div>
            {m.role === "assistant" && Array.isArray(m.suggestions) && m.suggestions.length > 0 && (
              <SuggestionList
                suggestions={m.suggestions}
                msg={m}
                msgIndex={i}
                onTrackClick={onTrackClick}
              />
            )}
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="chatbot-typing">
            <div className="bg-rp-bg border border-rp-border rounded-2xl px-3 py-2 text-sm text-rp-textDim flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              {activePersonaName || "Mentor"} is typing…
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
